import { useState } from 'react';

import { Download } from 'lucide-react';

import { IdeaAttachment, IdeaTier } from '@/types/service/ideaExchange';

import {
  downloadIdeaAttachment,
  formatFileSize,
  ideaAttachmentErrorMessage,
} from '@/lib/ideaAttachment';

import { Button } from '@/components/ui/button';

interface IdeaAttachmentsProps {
  requestId: number;
  tier: IdeaTier;
  attachments: IdeaAttachment[];
}

export function IdeaAttachments({ requestId, tier, attachments }: IdeaAttachmentsProps) {
  const [downloading, setDownloading] = useState<number>();
  const [error, setError] = useState('');

  if (!attachments.length) return null;

  async function download(attachment: IdeaAttachment) {
    if (downloading !== undefined) return;
    setDownloading(attachment.id);
    setError('');
    try {
      await downloadIdeaAttachment(requestId, tier, attachment);
    } catch (caught) {
      setError(await ideaAttachmentErrorMessage(caught));
    } finally {
      setDownloading(undefined);
    }
  }

  return (
    <section className='space-y-2' aria-labelledby='idea-attachments-heading'>
      <h2 id='idea-attachments-heading' className='font-semibold'>
        Attachments
      </h2>
      <ul className='space-y-2'>
        {attachments.map((attachment) => (
          <li key={attachment.id} className='flex flex-wrap items-baseline gap-x-2 text-sm'>
            <Button
              variant='link'
              className='h-auto max-w-full whitespace-normal break-all p-0 text-left text-foreground underline'
              onClick={() => download(attachment)}
            >
              <Download className='mr-1 size-4 shrink-0' aria-hidden />
              {attachment.file_name}
            </Button>
            <span className='text-muted-foreground'>
              {formatFileSize(attachment.byte_size)} · {attachment.created_by_name} ·{' '}
              {new Date(attachment.created_at).toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
      {downloading !== undefined && (
        <p role='status' className='text-sm text-muted-foreground'>
          Downloading...
        </p>
      )}
      {error && (
        <p role='alert' className='text-sm text-destructive'>
          {error}
        </p>
      )}
    </section>
  );
}
